import { ReportsModule } from "@/modules/reports-objective";
import { Vue, Component, Ref } from "vue-property-decorator";
import Grid from "@/components/wijmo-grid/grid/grid.vue";
import { VersionGroupModule } from "@/modules/versiongroup";
import { disabledDueToAccessLevel, getRouteAccessLevel } from "@/util/func";
import { CurriculumType, GLGlobal, MessageHelper, NotificationType, fmtMsg } from "commonui";
import { MasteryObjectiveReportLocale } from "@/locales/localeid";
import { LessonPlanVersionStatusMap, MethodologyTypeItems, NoneVersionGroup } from "@/util/constants";
import { AccessLevel, ExportType, ServerResponseCode } from "@/util/enum";
import { GridRef } from "@/components/wijmo-grid/grid/grid";
import { MasteryQuestionReportExportParamsModel, ObjectiveService } from "@/services/objective-manager";
import MasteryQuestionObjectiveHeaderActions from "../../components/mastery-question-objective-header-actions/mastery-question-objective-header-actions.vue";
import { Button } from "ant-design-vue";
import { BlobHelper } from "@/util/helper";
import { VCSLevelModel } from "@/models/title-list-model";
import { CellType } from "wijmo/wijmo.grid";
import { setAttribute } from "wijmo/wijmo";

export interface GridData {
	unit: {
		displayName: string | number;
		sequence: string | number;
	};
	component: string;
	levelNumber: string;
	level1: string;
	level2: string;
	level3: string;
	contentExposureSequence: number;
	lessonPlanVersionName: string;
	methodology: string | undefined;
	teachingActivityText: string;
}

export enum UnitDataMapper {
	DisplayNameProp = 1,
	UnitProp = 2
}
@Component({
	components: { Grid, MasteryQuestionObjectiveHeaderActions, Button }
})
export default class MasteryQuestionToObjective extends Vue {
	@Ref() readonly gridRef!: GridRef;
	accessLevel = AccessLevel;
	public currentPage: number = 1;
	private objectiveService = ObjectiveService;
	public versionGroupModule = VersionGroupModule;
	public format = fmtMsg;
	public enableExportButton: boolean = false;
	public locale = MasteryObjectiveReportLocale;

	get isGridLoading() {
		return ReportsModule.componentObjectiveGridLoading;
	}

	getLevelNumber(sequenceArray) {
		return sequenceArray.filter((ele) => !!ele).join(".");
	}

	get currentRouteAccessLevel() {
		return getRouteAccessLevel(this.$route);
	}

	get disabledDueToAccessLevel() {
		return disabledDueToAccessLevel(this.currentRouteAccessLevel);
	}

	gridData() {
		let data: GridData[] = ReportsModule.masteryQuestionObjectiveReportData.map((currentElement: any) => {
			const currentRow = {
				unit: {
					displayName: this.getUnitData(currentElement.unitId, UnitDataMapper.DisplayNameProp),
					sequence: this.getUnitData(currentElement.unitId, UnitDataMapper.UnitProp)
				},
				component: currentElement.contentName,
				levelNumber: this.getLevelNumber([
					currentElement.level1Sequence,
					currentElement.level2Sequence,
					currentElement.level3Sequence
				]),
				level1: currentElement.level1Text,
				level2: currentElement.level2Text,
				level3: currentElement.level3Text,
				contentExposureSequence: currentElement.contentExposureSequence,
				lessonPlanVersionName:
					currentElement.lessonPlanVersionName +
					" (" +
					LessonPlanVersionStatusMap.get(currentElement.lessonPlanVersionStatus) +
					")",
				methodology: MethodologyTypeItems.get(currentElement.methodology),
				teachingActivityText: currentElement.teachingActivityText
			};
			return currentRow;
		});

		const allLevelNumbers = data.map((d) => d.levelNumber);
		const sortedLevelNumbers = allLevelNumbers.slice().sort(this.customSort);

		data = data.map((d) => {
			return {
				...d,
				levelWeight: sortedLevelNumbers.findIndex((sln) => sln === d.levelNumber)
			};
		});

		return data;
	}

	getUnitData(unitId: number, ele: UnitDataMapper) {
		const selectedVersionGroup =
			VersionGroupModule.selectedGsVersion == NoneVersionGroup
				? CurriculumType.LittleSEED
				: CurriculumType.GrapeSEED;
		const res: VCSLevelModel[] = ReportsModule.vcsUnits.filter(
			(unit) => unit.curriculumType === selectedVersionGroup && unitId === unit.id
		);
		if (ele == UnitDataMapper.DisplayNameProp) {
			return res[0].displayName;
		}
		return res[0].unit;
	}

	customSort(a, b) {
		const versionA = a.split(".").map(Number);
		const versionB = b.split(".").map(Number);

		for (let i = 0; i < Math.max(versionA.length, versionB.length); i++) {
			const partA = versionA[i] || 0;
			const partB = versionB[i] || 0;

			if (partA < partB) return -1;
			if (partA > partB) return 1;
		}

		return 0;
	}

	formatData(data) {
		if (!data.reportData.length) {
			//If we are on the first page so we don't need to go back to previous page
			this.enableExportButton = false;
			if (this.currentPage > 1) {
				this.currentPage = this.currentPage - 1;
				this.gridRef.setCurrentPage(this.currentPage);
				this.gridRef.reload();
			}
		} else {
			this.enableExportButton = true;
		}

		let gridData: GridData[] = data.reportData.map((currentElement: any) => {
			const currentRow = {
				unit: {
					displayName: this.getUnitData(currentElement.unitId, UnitDataMapper.DisplayNameProp),
					sequence: this.getUnitData(currentElement.unitId, UnitDataMapper.UnitProp)
				},
				component: currentElement.contentName,
				levelNumber: this.getLevelNumber([
					currentElement.level1Sequence,
					currentElement.level2Sequence,
					currentElement.level3Sequence
				]),
				level1: currentElement.level1Text,
				level2: currentElement.level2Text,
				level3: currentElement.level3Text,
				contentExposureSequence: currentElement.contentExposureSequence,
				lessonPlanVersionName:
					currentElement.lessonPlanVersionName +
					" (" +
					LessonPlanVersionStatusMap.get(currentElement.lessonPlanVersionStatus) +
					")",
				methodology: MethodologyTypeItems.get(currentElement.methodology),
				teachingActivityText: currentElement.teachingActivityText
			};
			return currentRow;
		});

		const allLevelNumbers = gridData.map((d) => d.levelNumber);
		const sortedLevelNumbers = allLevelNumbers.slice().sort(this.customSort);

		gridData = gridData.map((d) => {
			return {
				...d,
				levelWeight: sortedLevelNumbers.findIndex((sln) => sln === d.levelNumber)
			};
		});

		return gridData;
	}

	handlePaginationChange(currentPage: number) {
		this.currentPage = currentPage;
	}

	get gridDataServiceFunc() {
		return this.objectiveService.getMasteryObjectiveReport;
	}

	get persistedCurrentPage() {
		return this.currentPage;
	}

	getGridDataParams() {
		return {
			versionGroupId:
				this.versionGroupModule.selectedGsVersion || this.versionGroupModule.selectedLsVersion || null,
			startUnitId: ReportsModule.unitRangeForReport.startUnitId,
			endUnitId: ReportsModule.unitRangeForReport.endUnitId,
			curriculumType:
				this.versionGroupModule.selectedGsVersion !== NoneVersionGroup
					? CurriculumType.GrapeSEED
					: this.versionGroupModule.selectedLsVersion !== NoneVersionGroup
					? CurriculumType.LittleSEED
					: null,
			contentId: ReportsModule.selectedContentIdForReport,
			objectiveId: ReportsModule.selectedObjectiveIdForReport
		};
	}

	getReportData() {
		if (this.gridRef) {
			this.gridRef.reload();
			this.gridRef.setCurrentPage(1);
		}
	}

	handleExport(event: any) {
		const startUnit = ReportsModule.vcsUnits.find((u) => u.id === ReportsModule.unitRangeForReport.startUnitId)
			?.unit as number;
		const endUnit = ReportsModule.vcsUnits.find((u) => u.id === ReportsModule.unitRangeForReport.endUnitId)
			?.unit as number;

		const params: MasteryQuestionReportExportParamsModel = {
			versionGroupId: this.versionGroupModule.selectedGsVersion || this.versionGroupModule.selectedLsVersion,
			startUnitId: ReportsModule.unitRangeForReport.startUnitId,
			endUnitId: ReportsModule.unitRangeForReport.endUnitId,
			curriculumType:
				this.versionGroupModule.selectedGsVersion !== NoneVersionGroup
					? CurriculumType.GrapeSEED
					: CurriculumType.LittleSEED,
			contentId: ReportsModule.selectedContentIdForReport,
			objectiveId: ReportsModule.selectedObjectiveIdForReport,
			email: GLGlobal.loginInfo()?.profile?.email,
			isLazyExport: endUnit - startUnit >= 5,
			exportType: event.key === ExportType.Excel ? ExportType.Excel : ExportType.Pdf
		};
		ObjectiveService.exportMasteryObjectiveReport(params).then((data) => {
			if (data.status === ServerResponseCode.Ok) {
				BlobHelper.saveFile(data);
			} else if (data.status === ServerResponseCode.Created) {
				MessageHelper.Message(NotificationType.Success, fmtMsg(MasteryObjectiveReportLocale.MOExportFileMsg));
			}
		});
	}

	beforeDestroy() {
		ReportsModule.setMasteryQuestionObjectiveReportData([]);
	}

	initGrid(grid) {
		grid.formatItem.addHandler((s, e) => {
			if (e.panel.cellType == CellType.ColumnHeader) {
				setAttribute(e.cell, "title", e.cell.outerText);
			}
		});
	}
}
